const POSTER_MAX_EDGE = 1280
const POSTER_QUALITY = 0.8
const POSTER_FALLBACK_QUALITY = 0.5
const POSTER_MAX_BYTES = 2 * 1024 * 1024
const TIMEOUT_MS = 10_000

export interface VideoCapture {
  poster: Blob
  posterMime: 'image/jpeg'
  /** Pixel size of the video; 0 when unknown. */
  width: number
  height: number
  durationSeconds: number | null
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality))
}

/**
 * Captures a poster frame from a local video file, using an object (blob:) URL and a muted
 * metadata-only video element. Resolves null on a decode error or after 10 seconds: the
 * upload then continues without a poster.
 */
export function captureVideoPoster(file: Blob): Promise<VideoCapture | null> {
  return new Promise<VideoCapture | null>((resolve) => {
    const url = URL.createObjectURL(file)
    const video = document.createElement('video')
    let settled = false

    const finish = (result: VideoCapture | null): void => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      video.removeAttribute('src')
      video.load()
      URL.revokeObjectURL(url)
      resolve(result)
    }

    const timer = window.setTimeout(() => finish(null), TIMEOUT_MS)

    video.muted = true
    video.preload = 'metadata'
    video.playsInline = true
    video.addEventListener('error', () => finish(null))

    video.addEventListener('loadedmetadata', () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0
      const seekTo = duration > 0 ? Math.min(1, duration * 0.1) : 0
      video.currentTime = seekTo
    })

    video.addEventListener('seeked', () => {
      void (async (): Promise<void> => {
        try {
          const vw = video.videoWidth
          const vh = video.videoHeight
          if (vw <= 0 || vh <= 0) {
            finish(null)
            return
          }
          const scale = Math.min(1, POSTER_MAX_EDGE / Math.max(vw, vh))
          const canvas = document.createElement('canvas')
          canvas.width = Math.max(1, Math.round(vw * scale))
          canvas.height = Math.max(1, Math.round(vh * scale))
          const ctx = canvas.getContext('2d')
          if (ctx === null) {
            finish(null)
            return
          }
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
          let poster = await toBlob(canvas, POSTER_QUALITY)
          if (poster !== null && poster.size > POSTER_MAX_BYTES) {
            poster = await toBlob(canvas, POSTER_FALLBACK_QUALITY)
          }
          if (poster === null || poster.size > POSTER_MAX_BYTES) {
            finish(null)
            return
          }
          finish({
            poster,
            posterMime: 'image/jpeg',
            width: vw,
            height: vh,
            durationSeconds: Number.isFinite(video.duration) && video.duration > 0 ? Math.round(video.duration) : null,
          })
        } catch {
          finish(null)
        }
      })()
    })

    video.src = url
  })
}
